import { Component, OnInit, OnDestroy, WritableSignal, signal } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { fetchAndActivate, getBoolean, getRemoteConfig, getString, RemoteConfig } from 'firebase/remote-config';
import { TranslateService } from '@ngx-translate/core';
import { fromEvent, map, merge, of, Subscription } from 'rxjs';
import { ApiService, ErrorResponse } from 'app/services/api.service';
import { environment } from 'environments/environment';
import Intercom from '@intercom/messenger-js-sdk';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false
})
export class AppComponent implements OnInit, OnDestroy {

  private networkStatusSubscription: Subscription = Subscription.EMPTY;
  private routerSubscription: Subscription = Subscription.EMPTY;
  private remoteConfig: RemoteConfig = getRemoteConfig();

  title: string = 'KeyToCheck';
  userLang: string = 'en';
  maintenance: WritableSignal<boolean> = signal(false);
  maintenance_msg: WritableSignal<any> = signal({});

  isApiOk: WritableSignal<boolean> = signal(true);
  refreshing: WritableSignal<boolean> = signal(false);
  networkStatus: WritableSignal<boolean> = signal(false);

  constructor(private translate: TranslateService, private apiService: ApiService, private router: Router) {
    this.userLang = navigator.language;

    this.translate.addLangs(environment.langs);
    this.translate.setFallbackLang('en');

    if (this.userLang && this.userLang.length >= 2) {
      this.userLang = this.userLang.slice(0, 2);
    }

    if (localStorage.getItem('language')) {
      this.userLang = localStorage.getItem('language');
    }

    if (environment.langs.includes(this.userLang)) {
      this.translate.use(this.userLang);
      localStorage.setItem('language', this.userLang);
    }
    else {
      this.userLang = 'fr';
      this.translate.use('fr');
      localStorage.setItem('language', 'fr');
    }

    if (environment.production) {
      Intercom({
        app_id: environment.intercom_app_id,
        Origin: 'KeyToCheck'
      });
    }

    this.routerSubscription = router.events.subscribe((val: any): void => {
      if (val instanceof NavigationStart) {
        this.getRemoteConfig();
        this.checkApi();
      }
    });
  }

  ngOnInit(): void {
    this.getRemoteConfig();
    this.checkApi();
    this.checkNetworkStatus();
  }

  ngOnDestroy(): void {
    this.networkStatusSubscription?.unsubscribe();
    this.routerSubscription?.unsubscribe();
  }

  getRemoteConfig(): void {
    const env: string = (environment.production ? '': '_dev');
    this.remoteConfig.settings.minimumFetchIntervalMillis = 30000;

    fetchAndActivate(this.remoteConfig)
      .then((): void => {
        this.maintenance.set(getBoolean(this.remoteConfig, 'maintenance' + env));
        this.maintenance_msg.set(JSON.parse(getString(this.remoteConfig, 'maintenance_msg' + env)));
      });
  }

  checkApi(): void {
    this.apiService.post('', {}, (_: any): void => {
      this.isApiOk.set(true);
      this.refreshing.set(false);
    }, (_: ErrorResponse): void => {
      this.isApiOk.set(false);
      this.refreshing.set(false);
    });
  }

  checkNetworkStatus(): void {
    this.networkStatus.set(navigator.onLine);

    this.networkStatusSubscription = merge(of(null), fromEvent(window, 'online'), fromEvent(window, 'offline'))
      .pipe(map((): boolean => navigator.onLine))
      .subscribe((status: boolean): void => {
        this.networkStatus.set(status);
      });
  }

  refresh(): void {
    this.refreshing.set(true);
    this.checkApi();
  }

}
